// src/components/layout/AthenaDialogStyles.ts
// ---------------------------------------------------------------------------
// Shared Athena Dialog styling utilities.
// Provides consistent glassmorphic styling for all modals/dialogs.
//
// Two variants:
//   1. "Light" (athena*Sx)  — light periwinkle body for read-only text content
//      Used by: LegalAcceptanceModal, DocumentPreviewModal
//   2. "Dark"  (athenaDark*Sx) — full dark gradient matching the onboarding
//      language-selection tooltip. Used for interactive modals with buttons,
//      chips, selects, checkboxes, or other selectable items.
//
// Rule: if the middle section contains only text → light variant.
//       if it has interactive controls → dark variant.
// ---------------------------------------------------------------------------

import { alpha, type SxProps, type Theme } from "@mui/material";
import { athenaColors } from "../../theme/palette";

// ---------------------------------------------------------------------------
// Dialog PaperProps.sx — light periwinkle card with glass border
// ---------------------------------------------------------------------------

export const athenaPaperSx: SxProps<Theme> = {
  borderRadius: 3,
  overflow: "hidden",
  background: athenaColors.lightPeriwinkle,
  border: `1px solid ${alpha(athenaColors.periwinkle, 0.5)}`,
  boxShadow: `0 24px 80px ${alpha(athenaColors.deepNavy, 0.35)}`,
  // Reset dark-dialog defaults so inputs render correctly on light bg
  "& .MuiOutlinedInput-root": {
    backgroundColor: athenaColors.white,
    "& fieldset": { borderColor: alpha(athenaColors.periwinkle, 0.4) },
    "&:hover fieldset": { borderColor: athenaColors.periwinkle },
    "&.Mui-focused fieldset": { borderColor: athenaColors.royalBlue },
  },
  "& .MuiOutlinedInput-input": {
    color: athenaColors.deepNavy,
  },
  "& .MuiInputLabel-root": {
    color: athenaColors.blueGray,
    "&.Mui-focused": { color: athenaColors.royalBlue },
  },
  "& .MuiCheckbox-root": {
    color: athenaColors.blueGray,
    "&.Mui-checked": { color: athenaColors.royalBlue },
  },
  "& .MuiSwitch-root .MuiSwitch-track": {
    backgroundColor: alpha(athenaColors.blueGray, 0.3),
  },
  "& .MuiSwitch-root .Mui-checked + .MuiSwitch-track": {
    backgroundColor: alpha(athenaColors.royalBlue, 0.5),
  },
  "& .MuiSelect-icon": {
    color: athenaColors.blueGray,
  },
  "& .MuiFormHelperText-root": {
    color: athenaColors.blueGray,
  },
};

// ---------------------------------------------------------------------------
// Backdrop blur
// ---------------------------------------------------------------------------

export const athenaBackdropSx: SxProps<Theme> = {
  backdropFilter: "blur(8px)",
  backgroundColor: alpha(athenaColors.black, 0.6),
};

// ---------------------------------------------------------------------------
// Dialog Title (gradient header)
// ---------------------------------------------------------------------------

export const athenaTitleSx: SxProps<Theme> = {
  display: "flex",
  alignItems: "center",
  gap: 1.5,
  pb: 1.5,
  background: `linear-gradient(135deg, ${athenaColors.deepNavy} 0%, ${athenaColors.royalBlue} 100%)`,
  borderBottom: `1px solid ${alpha(athenaColors.periwinkle, 0.2)}`,
  color: athenaColors.white,
  "& .MuiTypography-root": {
    color: athenaColors.white,
  },
};

// ---------------------------------------------------------------------------
// Dialog Content (light background)
// ---------------------------------------------------------------------------

export const athenaContentSx: SxProps<Theme> = {
  background: athenaColors.lightPeriwinkle,
  color: athenaColors.deepNavy,
  "& .MuiTypography-root": {
    color: athenaColors.deepNavy,
  },
  "& .MuiTypography-colorTextSecondary, & .MuiTypography-caption": {
    color: `${athenaColors.blueGray} !important`,
  },
  // Custom scrollbar
  "&::-webkit-scrollbar": { width: 6 },
  "&::-webkit-scrollbar-thumb": {
    backgroundColor: alpha(athenaColors.blueGray, 0.25),
    borderRadius: 3,
  },
  "&::-webkit-scrollbar-track": {
    backgroundColor: "transparent",
  },
};

// ---------------------------------------------------------------------------
// Dialog Actions (gradient footer)
// ---------------------------------------------------------------------------

export const athenaActionsSx: SxProps<Theme> = {
  px: 3,
  py: 2,
  background: `linear-gradient(135deg, ${athenaColors.deepNavy} 0%, ${athenaColors.royalBlue} 100%)`,
  borderTop: `1px solid ${alpha(athenaColors.periwinkle, 0.2)}`,
};

// ---------------------------------------------------------------------------
// Icon box in header (periwinkle icon container)
// ---------------------------------------------------------------------------

export const athenaHeaderIconBoxSx: SxProps<Theme> = {
  width: 40,
  height: 40,
  borderRadius: 2,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  background: alpha(athenaColors.periwinkle, 0.15),
  border: `1px solid ${alpha(athenaColors.periwinkle, 0.25)}`,
  flexShrink: 0,
};

// ---------------------------------------------------------------------------
// Button styles for footer
// ---------------------------------------------------------------------------

export const athenaPrimaryButtonSx: SxProps<Theme> = {
  borderRadius: 2,
  px: 3,
  py: 0.75,
  fontWeight: 700,
  textTransform: "none",
  fontSize: "0.9rem",
  backgroundColor: athenaColors.periwinkle,
  color: athenaColors.white,
  boxShadow: `0 2px 8px ${alpha(athenaColors.periwinkle, 0.4)}`,
  "&:hover": {
    backgroundColor: athenaColors.white,
    color: athenaColors.deepNavy,
  },
  "&.Mui-disabled": {
    backgroundColor: alpha(athenaColors.periwinkle, 0.2),
    color: alpha(athenaColors.periwinkle, 0.5),
  },
};

export const athenaSecondaryButtonSx: SxProps<Theme> = {
  borderRadius: 2,
  px: 2.5,
  py: 0.75,
  fontWeight: 600,
  textTransform: "none",
  fontSize: "0.88rem",
  color: athenaColors.periwinkle,
  borderColor: alpha(athenaColors.periwinkle, 0.4),
  "&:hover": {
    borderColor: athenaColors.periwinkle,
    background: alpha(athenaColors.periwinkle, 0.1),
  },
};

// ---------------------------------------------------------------------------
// Divider
// ---------------------------------------------------------------------------

export const athenaDividerSx: SxProps<Theme> = {
  borderColor: alpha(athenaColors.periwinkle, 0.3),
};

// ---------------------------------------------------------------------------
// Section card (for content grouping inside a dialog)
// ---------------------------------------------------------------------------

export const athenaSectionSx: SxProps<Theme> = {
  p: 2,
  borderRadius: 2,
  background: alpha(athenaColors.periwinkle, 0.12),
  border: `1px solid ${alpha(athenaColors.periwinkle, 0.25)}`,
};

// ═══════════════════════════════════════════════════════════════════════════
// DARK VARIANT — full gradient, matching the onboarding language-selection
// tooltip. Used for interactive modals (buttons, chips, inputs, selects).
// ═══════════════════════════════════════════════════════════════════════════

const darkGradientBg = `linear-gradient(160deg, ${athenaColors.deepNavy} 0%, ${alpha(
  athenaColors.royalBlue,
  0.95
)} 100%)`;

// ---------------------------------------------------------------------------
// Dark: Dialog PaperProps.sx
// ---------------------------------------------------------------------------

export const athenaDarkPaperSx: SxProps<Theme> = {
  borderRadius: 3,
  overflow: "hidden",
  background: darkGradientBg,
  border: `1px solid ${alpha(athenaColors.periwinkle, 0.3)}`,
  boxShadow: `0 24px 80px ${alpha(
    athenaColors.deepNavy,
    0.5
  )}, 0 0 30px ${alpha(athenaColors.royalBlue, 0.2)}`,
  // Inputs on dark background
  "& .MuiOutlinedInput-root": {
    backgroundColor: alpha(athenaColors.white, 0.07),
    color: athenaColors.white,
    "& fieldset": { borderColor: alpha(athenaColors.periwinkle, 0.3) },
    "&:hover fieldset": { borderColor: alpha(athenaColors.periwinkle, 0.55) },
    "&.Mui-focused fieldset": { borderColor: athenaColors.periwinkle },
  },
  "& .MuiOutlinedInput-input": {
    color: athenaColors.white,
    "&::placeholder": { color: alpha(athenaColors.white, 0.45), opacity: 1 },
  },
  "& .MuiInputLabel-root": {
    color: alpha(athenaColors.white, 0.6),
    "&.Mui-focused": { color: athenaColors.periwinkle },
  },
  "& .MuiCheckbox-root": {
    color: alpha(athenaColors.white, 0.5),
    "&.Mui-checked": { color: athenaColors.periwinkle },
  },
  "& .MuiSwitch-root .MuiSwitch-track": {
    backgroundColor: alpha(athenaColors.white, 0.2),
  },
  "& .MuiSwitch-root .Mui-checked + .MuiSwitch-track": {
    backgroundColor: alpha(athenaColors.periwinkle, 0.5),
  },
  "& .MuiSelect-icon": {
    color: alpha(athenaColors.white, 0.5),
  },
  "& .MuiFormHelperText-root": {
    color: alpha(athenaColors.white, 0.5),
  },
  "& .MuiSvgIcon-root": {
    color: alpha(athenaColors.white, 0.5),
  },
  "& .MuiAutocomplete-popupIndicator": { color: alpha(athenaColors.white, 0.4) },
  "& .MuiAutocomplete-clearIndicator": { color: alpha(athenaColors.white, 0.4) },
  // Stepper — match the Language-Selection tooltip language: periwinkle for the
  // active / completed steps, dim white for pending ones. Without these
  // overrides the labels render as default text.primary (dark navy) which is
  // invisible against the gradient. See cursor_logs/036 §9.
  "& .MuiStepLabel-label": {
    color: alpha(athenaColors.white, 0.55),
    fontWeight: 500,
    "&.Mui-active": {
      color: athenaColors.white,
      fontWeight: 700,
    },
    "&.Mui-completed": {
      color: alpha(athenaColors.white, 0.85),
      fontWeight: 600,
    },
  },
  "& .MuiStepIcon-root": {
    color: alpha(athenaColors.white, 0.25),
    "&.Mui-active": { color: athenaColors.periwinkle },
    "&.Mui-completed": { color: athenaColors.periwinkle },
  },
  "& .MuiStepIcon-text": {
    fill: athenaColors.deepNavy,
    fontWeight: 700,
  },
  "& .MuiStepConnector-line": {
    borderColor: alpha(athenaColors.periwinkle, 0.25),
  },
  // Inner Paper / Card surfaces inside the wizard body. The wizard was
  // designed against a light page background so it uses
  // `alpha(theme.palette.background.paper, 0.5)` for its internal panels —
  // which on top of the dark gradient resolves to a translucent grey-blue
  // rectangle and washes out body text. Override the Paper bg to a subtle
  // periwinkle tint that reads cleanly on the gradient.
  "& .MuiPaper-root": {
    backgroundColor: alpha(athenaColors.periwinkle, 0.08),
    backgroundImage: "none",
    color: athenaColors.white,
  },
  // Body text inside the wizard — default text.secondary becomes nearly black
  // against the gradient; promote it to a soft white instead.
  "& .MuiTypography-body1, & .MuiTypography-body2": {
    color: alpha(athenaColors.white, 0.92),
  },
  // Table preview rows (used in the file-upload step)
  "& .MuiTable-root": { color: athenaColors.white },
  "& .MuiTableCell-root": {
    color: alpha(athenaColors.white, 0.9),
    borderColor: alpha(athenaColors.periwinkle, 0.2),
  },
  "& .MuiTableCell-head": {
    color: athenaColors.white,
    fontWeight: 700,
    backgroundColor: alpha(athenaColors.periwinkle, 0.18),
  },
  // Dividers
  "& .MuiDivider-root": { borderColor: alpha(athenaColors.periwinkle, 0.2) },
};

// ---------------------------------------------------------------------------
// Dark: Dialog Title (blends into gradient — no separate bg)
// ---------------------------------------------------------------------------

export const athenaDarkTitleSx: SxProps<Theme> = {
  display: "flex",
  alignItems: "center",
  gap: 1.5,
  pb: 1,
  pt: 2.5,
  px: 3,
  color: athenaColors.white,
  "& .MuiTypography-root": {
    color: athenaColors.white,
  },
};

// ---------------------------------------------------------------------------
// Dark: Dialog Content (transparent — gradient shows through)
// ---------------------------------------------------------------------------

export const athenaDarkContentSx: SxProps<Theme> = {
  px: 3,
  pt: 1,
  pb: 2,
  color: athenaColors.white,
  "& .MuiTypography-root": {
    color: athenaColors.white,
  },
  "& .MuiTypography-colorTextSecondary, & .MuiTypography-caption": {
    color: `${alpha(athenaColors.white, 0.6)} !important`,
  },
  "& .MuiFormControlLabel-label": {
    color: athenaColors.white,
  },
  // Custom scrollbar (light on dark)
  "&::-webkit-scrollbar": { width: 6 },
  "&::-webkit-scrollbar-thumb": {
    backgroundColor: alpha(athenaColors.periwinkle, 0.3),
    borderRadius: 3,
  },
  "&::-webkit-scrollbar-track": {
    backgroundColor: "transparent",
  },
};

// ---------------------------------------------------------------------------
// Dark: Dialog Actions (subtle border, blends with gradient)
// ---------------------------------------------------------------------------

export const athenaDarkActionsSx: SxProps<Theme> = {
  px: 3,
  py: 2,
  borderTop: `1px solid ${alpha(athenaColors.periwinkle, 0.15)}`,
};

// ---------------------------------------------------------------------------
// Dark: Secondary button (ghost on dark bg)
// ---------------------------------------------------------------------------

export const athenaDarkSecondaryButtonSx: SxProps<Theme> = {
  borderRadius: 2,
  px: 2.5,
  py: 0.75,
  fontWeight: 600,
  textTransform: "none",
  fontSize: "0.88rem",
  color: alpha(athenaColors.white, 0.5),
  "&:hover": {
    color: alpha(athenaColors.white, 0.8),
    background: alpha(athenaColors.white, 0.05),
  },
};

// ---------------------------------------------------------------------------
// Dark: Primary button (matches onboarding language-selection "Continue")
// ---------------------------------------------------------------------------

export const athenaDarkPrimaryButtonSx: SxProps<Theme> = {
  borderRadius: 2,
  px: 3,
  py: 0.75,
  fontWeight: 600,
  textTransform: "none",
  fontSize: "0.8rem",
  minWidth: 80,
  backgroundColor: athenaColors.periwinkle,
  // deepNavy, NOT white. periwinkle (#b7c1de) is a LIGHT fill: a white label on
  // it is 1.80:1, below even the 3:1 large-text floor, and this style is the
  // primary button of every dark dialog in the app. deepNavy on periwinkle is
  // 8.93:1. Icons follow via `color: inherit` below.
  color: athenaColors.deepNavy,
  "& .MuiButton-endIcon, & .MuiButton-startIcon": {
    color: "inherit",
  },
  "&:hover": {
    backgroundColor: alpha(athenaColors.periwinkle, 0.85),
  },
  // Disabled still has to be READABLE, just clearly inert: periwinkle-on-
  // periwinkle resolved to 2.30:1 against the dialog paper. White at 50% gives
  // 3.37:1 and still reads as disabled.
  "&.Mui-disabled": {
    backgroundColor: alpha(athenaColors.periwinkle, 0.2),
    color: alpha(athenaColors.white, 0.5),
  },
};

// ---------------------------------------------------------------------------
// Dark: Divider
// ---------------------------------------------------------------------------

export const athenaDarkDividerSx: SxProps<Theme> = {
  borderColor: alpha(athenaColors.periwinkle, 0.2),
};

// ---------------------------------------------------------------------------
// Dark: Section card (for content grouping inside a dark dialog)
// ---------------------------------------------------------------------------

export const athenaDarkSectionSx: SxProps<Theme> = {
  p: 2,
  borderRadius: 2,
  background: alpha(athenaColors.periwinkle, 0.1),
  border: `1px solid ${alpha(athenaColors.periwinkle, 0.2)}`,
};

// Color exports for direct use
export { athenaColors };
